import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App,
  Avatar,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  fetchTrtcGroups,
  applyTrtcJoinGroupBatch,
  TrtcGroup,
} from '../services/botTrtcGroupApi';
import { getEmployeeList, BotEmployee } from '../services/botEmployeeApi';

const { Text, Paragraph } = Typography;

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
};

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ marginBottom: 24 }}>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
      <TeamOutlined style={{ marginRight: 8, color: COLORS.primary }} />
      {title}
    </h2>
    <div
      style={{
        width: 40,
        height: 3,
        background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

const APPLY_OPTION_TAG: Record<string, { color: string; text: string }> = {
  FreeAccess: { color: 'green', text: '自由加入' },
  NeedPermission: { color: 'gold', text: '审批后加入' },
  DisableApply: { color: 'red', text: '禁止申请' },
};

const PAGE_SIZE = 12;

const BotGroupManagement: React.FC = () => {
  const { message } = App.useApp();

  const [employees, setEmployees] = useState<BotEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);

  const [groups, setGroups] = useState<TrtcGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyTargetGroup, setApplyTargetGroup] = useState<TrtcGroup | null>(null);
  const [applyForm] = Form.useForm<{ employeeIds?: number[]; reason?: string }>();
  const [applying, setApplying] = useState(false);

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.id,
        label: (
          <Space size={6}>
            {e.avatar ? <Avatar size={20} src={e.avatar} /> : <Avatar size={20} icon={<UserOutlined />} />}
            <span>{e.userName || `bot#${e.botId}`}</span>
            {!e.isLoggedIn && (
              <Tag color="red" style={{ marginInlineStart: 0 }}>
                未登录
              </Tag>
            )}
          </Space>
        ),
        disabled: !e.isLoggedIn,
      })),
    [employees],
  );

  const fetchEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const list = await getEmployeeList();
      setEmployees(list);
      // 没选员工时，默认选第一个已登录的
      setSelectedEmployeeId((prev) => {
        if (prev != null && list.some((e) => e.id === prev)) return prev;
        const firstActive = list.find((e) => e.isLoggedIn);
        return firstActive ? firstActive.id : undefined;
      });
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setEmployeesLoading(false);
    }
  }, [message]);

  const fetchGroups = useCallback(
    async (p = page, kw = appliedKeyword, empId = selectedEmployeeId) => {
      setGroupsLoading(true);
      try {
        const data = await fetchTrtcGroups({
          page: p,
          limit: PAGE_SIZE,
          employeeId: empId ?? null,
          groupName: kw,
        });
        setGroups(data.list || []);
        setTotal(Number(data.totalCount) || 0);
      } catch (e: any) {
        message.error(e.message);
        setGroups([]);
        setTotal(0);
      } finally {
        setGroupsLoading(false);
      }
    },
    [page, appliedKeyword, selectedEmployeeId, message],
  );

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // employee 切换 / 关键词应用 / 翻页 → 重新拉
  useEffect(() => {
    fetchGroups(page, appliedKeyword, selectedEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedKeyword, selectedEmployeeId]);

  const handleSearch = () => {
    setPage(1);
    setAppliedKeyword(keyword.trim());
  };

  const handleReload = () => {
    fetchGroups(page, appliedKeyword, selectedEmployeeId);
  };

  const openApplyModal = (group: TrtcGroup) => {
    setApplyTargetGroup(group);
    applyForm.resetFields();
    applyForm.setFieldsValue({
      employeeIds: selectedEmployeeId ? [selectedEmployeeId] : [],
    });
    setApplyModalOpen(true);
  };

  const handleApply = async () => {
    if (!applyTargetGroup) return;
    try {
      const values = await applyForm.validateFields();
      setApplying(true);
      const ret = await applyTrtcJoinGroupBatch({
        employeeIds: values.employeeIds!,
        groupId: applyTargetGroup.groupId,
        reason: values.reason,
      });

      // 失败的明细 → error notification（每个一条，避免叠堆）
      const failed = ret.results.filter((r) => !r.success);
      const succeed = ret.results.filter((r) => r.success);

      if (succeed.length > 0) {
        message.success(`${succeed.length}/${ret.total} 个员工申请成功`);
      }
      failed.forEach((r) => {
        const name = r.botUserName || `employeeId=${r.employeeId}`;
        message.error(`${name}: ${r.error || '申请失败'}`);
      });
      if (succeed.length === 0 && failed.length === 0) {
        message.warning('未提交任何申请');
      }

      if (failed.length === 0) {
        setApplyModalOpen(false);
      }
      // 刷新当前页（更新 isUserInGroup / hasPendingApplication）
      fetchGroups(page, appliedKeyword, selectedEmployeeId);
    } catch (e: any) {
      if (e?.errorFields) return; // 校验失败
      message.error(e.message || '申请失败');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      <PageHeader title="机器人群管理" />

      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Space wrap size={12}>
          <Space size={6}>
            <span style={{ color: 'rgba(0,0,0,0.65)' }}>当前员工：</span>
            <Select
              value={selectedEmployeeId}
              onChange={(v) => setSelectedEmployeeId(v)}
              options={employeeOptions}
              loading={employeesLoading}
              placeholder="选择专属员工"
              style={{ minWidth: 220 }}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Space>
          <Input
            allowClear
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            placeholder="按群名搜索"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReload}>
            刷新
          </Button>
          <Text type="secondary">共 {total} 个群组</Text>
        </Space>
      </Card>

      <Spin spinning={groupsLoading}>
        {groups.length === 0 && !groupsLoading ? (
          <Empty description="暂无群聊" />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            {groups.map((g) => {
              const optionTag = g.applyJoinOption
                ? APPLY_OPTION_TAG[g.applyJoinOption] || {
                    color: 'default',
                    text: g.applyJoinOption,
                  }
                : null;
              return (
                <Card
                  key={g.groupId}
                  styles={{ body: { padding: 16 } }}
                  hoverable
                >
                  <Space align="start" size={12} style={{ width: '100%' }}>
                    <Avatar
                      size={56}
                      src={g.faceUrl || undefined}
                      icon={<TeamOutlined />}
                      style={{ background: '#f0f5ff', color: COLORS.primary }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Space wrap size={6} style={{ marginBottom: 4 }}>
                        <Text strong ellipsis style={{ fontSize: 15 }}>
                          {g.groupName || g.groupId}
                        </Text>
                        {optionTag && <Tag color={optionTag.color}>{optionTag.text}</Tag>}
                        {g.isUserInGroup && <Tag color="blue">已在群中</Tag>}
                        {!g.isUserInGroup && g.hasPendingApplication && (
                          <Tag color="orange">审批中</Tag>
                        )}
                      </Space>
                      <div>
                        <Tooltip title={g.groupId}>
                          <Text type="secondary" copyable={{ text: g.groupId }} style={{ fontSize: 12 }}>
                            {g.groupId}
                          </Text>
                        </Tooltip>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
                        群主：{g.ownerName || '-'} · 成员 {g.memberCount}
                        {g.maxMemberCount ? `/${g.maxMemberCount}` : ''}
                      </div>
                      {g.introduction && (
                        <Paragraph
                          ellipsis={{ rows: 2, tooltip: g.introduction }}
                          type="secondary"
                          style={{ margin: '6px 0 0', fontSize: 12 }}
                        >
                          {g.introduction}
                        </Paragraph>
                      )}
                    </div>
                  </Space>
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={() => openApplyModal(g)}
                      disabled={g.applyJoinOption === 'DisableApply'}
                    >
                      专属员工加入
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Spin>

      {total > 0 && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            onChange={(p) => setPage(p)}
          />
        </div>
      )}

      <Modal
        title={applyTargetGroup ? `申请加入：${applyTargetGroup.groupName || applyTargetGroup.groupId}` : '专属员工加入'}
        open={applyModalOpen}
        onCancel={() => setApplyModalOpen(false)}
        onOk={handleApply}
        okText="提交申请"
        confirmLoading={applying}
        destroyOnClose
      >
        <Form form={applyForm} layout="vertical" preserve={false}>
          <Form.Item
            label="选择专属员工（可多选）"
            name="employeeIds"
            rules={[{ required: true, message: '请至少选择一个专属员工' }]}
          >
            <Select
              mode="multiple"
              placeholder="可选多个员工，提交后会逐个申请加入"
              options={employeeOptions}
              loading={employeesLoading}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item label="申请理由（可选）" name="reason" extra="所有选中员工共用同一份申请理由">
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="对该群感兴趣……（最多 500 字符）"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BotGroupManagement;
